import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCurrentCashSession,
  openCashSession,
} from '../api/posApi';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR');
  } catch (error) {
    return value;
  }
};

const CashSessionGate = ({ storeId, onSessionReady, children, hideHeader = false }) => {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading');
  const [openAmount, setOpenAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchCurrentSession = useCallback(async () => {
    if (!storeId) {
      setStatus('missing-store');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const data = await getCurrentCashSession(storeId);
      setSession(data);
      setStatus('ready');
      onSessionReady?.(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setStatus('no-session');
      } else {
        setStatus('error');
        setError(err?.response?.data?.message || err.message);
      }
    }
  }, [onSessionReady, storeId]);

  useEffect(() => {
    fetchCurrentSession();
  }, [fetchCurrentSession]);

  const handleOpenSession = async () => {
    if (!openAmount) return;
    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        open_amount: Number(openAmount),
        notes: notes || undefined,
      };
      const data = await openCashSession(storeId, payload);
      setSession(data);
      setStatus('ready');
      onSessionReady?.(data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sessionStatus = useMemo(() => session?.status || '-', [session]);

  if (status === 'loading') {
    return (
      <div className="container py-5 text-center">
        <h2>Chargement de la session caisse...</h2>
        <p>Verification de la session active pour ce magasin.</p>
      </div>
    );
  }

  if (status === 'missing-store') {
    return (
      <div className="container py-5 text-center">
        <h2>Magasin non defini</h2>
        <p>Merci de selectionner un magasin avant d'ouvrir une session caisse.</p>
      </div>
    );
  }

  if (status === 'no-session') {
    return (
      <div className="container py-5">
        <div className="card shadow-sm p-4 text-center">
          <h2 className="mb-3">Aucune session caisse active</h2>
          <p className="text-muted">Ouvrez une session pour demarrer les ventes.</p>
          <div className="d-flex flex-column align-items-center gap-3 mt-3">
            <input
              type="number"
              className="form-control text-center"
              style={{ maxWidth: 240 }}
              placeholder="Montant d'ouverture"
              value={openAmount}
              onChange={(event) => setOpenAmount(event.target.value)}
            />
            <textarea
              className="form-control"
              style={{ maxWidth: 360 }}
              placeholder="Notes (optionnel)"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            {error && <div className="text-danger">{error}</div>}
            <button
              className="btn btn-primary btn-lg"
              onClick={handleOpenSession}
              disabled={isSubmitting || !openAmount}
            >
              {isSubmitting ? 'Ouverture...' : 'Ouvrir la session'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="container py-5 text-center">
        <h2>Erreur de session caisse</h2>
        <p className="text-danger">{error || 'Impossible de recuperer la session.'}</p>
        <button className="btn btn-outline-primary" onClick={fetchCurrentSession}>
          Reessayer
        </button>
      </div>
    );
  }

  if (session?.status === 'CLOSED') {
    return (
      <div className="container py-5 text-center">
        <h2>Session caisse cloturee</h2>
        <p>Les ventes sont verrouillees. Veuillez recharger l'application.</p>
      </div>
    );
  }

  if (hideHeader) {
    return <>{children}</>;
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <div className="text-muted">Magasin</div>
          <div className="fw-bold">{storeId}</div>
        </div>
        <div>
          <span className={`badge ${sessionStatus === 'OPEN' ? 'bg-success' : 'bg-warning text-dark'}`}>
            {sessionStatus}
          </span>
        </div>
        <div>
          <div className="text-muted">Ouvert le</div>
          <div className="fw-bold">{formatDateTime(session?.opened_at)}</div>
        </div>
        <div>
          <div className="text-muted">Ouvert par</div>
          <div className="fw-bold">{session?.opened_by || '-'}</div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default CashSessionGate;
